import React from 'react';
import { useIntl } from 'react-intl';
import { DashboardWidget } from './DashboardWidget';
import { toCalendarDate, formatCalendarDate } from '../../../utils/UtilFunctions';

interface IPaymentStatus {
  clientId: number;
  clientName: string;
  isPaid: boolean;
  lastPaymentDate: string | null;
  nextPaymentDate: string | null;
  status: string | null;
  planPrice: number | null;
  paymentFrequency: string | null;
  planName: string | null;
}

interface Props {
  data: IPaymentStatus[];
  loading: boolean;
  onClientClick?: (clientId: number) => void;
}

export const PaymentDatesWidget: React.FC<Props> = ({ data, loading, onClientClick }) => {
  const intl = useIntl();

  // #38 ítem 1: las fechas de cobro son días-calendario guardados a medianoche UTC,
  // así que se comparan y se muestran como día, no como instante. Con el Date crudo
  // un cobro que vencía HOY ya daba "vencido" desde la madrugada, y encima se
  // mostraba el día anterior.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const DAYS_THRESHOLD_MS = 3 * 24 * 60 * 60 * 1000;
  const dueDate = (c: IPaymentStatus) => toCalendarDate(c.nextPaymentDate);
  // Solo clientes con suscripción ACTIVA y plan no per-session: el resto no
  // tiene fecha de cobro relevante (inactivos están cancelados/sin sub).
  const billable = data.filter((c) => c.paymentFrequency !== 'per_session' && c.status === 'Active');
  const overdue = billable.filter((c) => {
    const due = dueDate(c);
    return !c.isPaid && due !== null && due.getTime() < today.getTime();
  });
  // Próximos a cobrar: solo quienes pagan en <3 días (#6 ítem 5) — el resto no
  // aporta urgencia, ya se van a ver cuando se acerque la fecha.
  const upcoming = billable
    .filter((c) => {
      const due = dueDate(c);
      return (
        due !== null &&
        !overdue.find((o) => o.clientId === c.clientId) &&
        due.getTime() - today.getTime() < DAYS_THRESHOLD_MS
      );
    })
    .sort((a, b) => (dueDate(a)?.getTime() ?? 0) - (dueDate(b)?.getTime() ?? 0));

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return formatCalendarDate(dateStr, intl.locale, { day: 'numeric', month: 'short' });
  };

  const formatPrice = (price: number | null) => {
    if (price === null || price === undefined) return '';
    return `$${price.toLocaleString()}`;
  };

  const renderRow = (client: IPaymentStatus, isOverdue: boolean) => (
    <div
      key={client.clientId}
      className="flex align-items-center gap-2"
      style={{
        padding: '0.5rem',
        borderRadius: 'var(--ios-radius-md)',
        background: isOverdue ? 'rgba(239,68,68,0.08)' : 'var(--ios-surface-subtle)',
        borderLeft: isOverdue ? '3px solid #ef4444' : '3px solid transparent',
        cursor: onClientClick ? 'pointer' : 'default'
      }}
      onClick={() => onClientClick?.(client.clientId)}
    >
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: '0.82rem',
            color: 'var(--ios-text)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {client.clientName}
        </div>
        <div style={{ fontSize: '0.72rem', color: 'var(--ios-text-tertiary)' }}>
          {formatDate(client.nextPaymentDate)}
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        {client.planPrice !== null && (
          <div style={{ fontWeight: 700, fontSize: '0.82rem', color: isOverdue ? '#ef4444' : '#22c55e' }}>
            {formatPrice(client.planPrice)}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <DashboardWidget
      title={intl.formatMessage({ id: 'dashboard.paymentDates' })}
      icon="pi pi-dollar"
      iconColor={overdue.length > 0 ? '#ef4444' : '#22c55e'}
      loading={loading}
      badge={overdue.length > 0 ? `${overdue.length} ${intl.formatMessage({ id: 'dashboard.overdue' })}` : null}
    >
      {billable.length === 0 ? (
        <p style={{ color: 'var(--ios-text-tertiary)', fontSize: '0.85rem', textAlign: 'center', padding: '1rem 0' }}>
          {intl.formatMessage({ id: 'dashboard.noPaymentData' })}
        </p>
      ) : (
        <div className="flex flex-column gap-2">
          {overdue.slice(0, 3).map((c) => renderRow(c, true))}
          {upcoming.slice(0, 4).map((c) => renderRow(c, false))}
        </div>
      )}
    </DashboardWidget>
  );
};
